<template>
  <div v-loading.fullscreen.lock="display.isLoading">
    <div class="centre">
      <h3>
        My Orders
        <el-button
          @click="onClickMyReservations"
          size="mini"
          class="bg-gold black"
          v-if="display.isSignedIn && !featureToggle.MEMBERSHIP"
        >View Reservations</el-button>
      </h3>
      <el-button class="bg-gold black" @click="onClickBack">Back</el-button>
      <el-button v-if="token" class="bg-black gold" @click="onClickUnlinkedOrder">Claim Order</el-button>
    </div>
    <el-dialog
      :visible.sync="display.dialog.unlinkedOrder.isVisible"
      :fullscreen="true"
      v-loading="display.isLoading"
    >
      <h3 class="centre">Orders To Be Claimed</h3>
      <p v-if="!unlinkedOrder" class="centre">No order.</p>
      <div v-else>
        <div class="order bg-gold black">
          <el-row class="order__section right">
            <el-col :span="24">
              <span
                class="order__status"
                :class="status[unlinkedOrder.status].styleClazz"
              >{{ status[unlinkedOrder.status].display }}</span>
            </el-col>
          </el-row>
          <el-row class="order__section metadata">
            <el-col :span="12">{{ unlinkedOrder.createdTimeDisplay }}</el-col>
            <el-col :span="12" class="right">{{ unlinkedOrder.orderNumber }}</el-col>
          </el-row>
          <el-row class="order__section right">
            <el-col :span="24">
              {{ mode[unlinkedOrder.mode] }} | {{ paymentMethod[unlinkedOrder.paymentMethod] }} |
              <strong>${{ unlinkedOrder.amountDisplay }}</strong>
            </el-col>
          </el-row>
          <el-row class="order__section">
            <el-col :span="12">{{ unlinkedOrder.customer.phone }}</el-col>
            <el-col
              :span="12"
              class="right"
            >{{ unlinkedOrder.customer.firstName }}, {{ unlinkedOrder.customer.surname }}</el-col>
          </el-row>
          <el-row class="order__section" v-if="unlinkedOrder.shippingAddress">
            <el-col
              :span="24"
            >{{ unlinkedOrder.shippingAddress.streetAddress }}, {{ unlinkedOrder.shippingAddress.city }}, {{ unlinkedOrder.shippingAddress.state }}, {{ unlinkedOrder.shippingAddress.zipCode }}</el-col>
          </el-row>
        </div>
      </div>
      <div class="centre">
        <el-button class="bg-black gold" v-if="unlinkedOrder" @click="onClickOwnOrder">Claim Order</el-button>
      </div>
    </el-dialog>
    <div class="order-list" v-infinite-scroll="loadNextPage" :infinite-scroll-immediate="true">
      <div
        class="order bg-gold black"
        v-for="order of orders"
        :key="order._id"
        @click="() => onClickOrder(order)"
      >
        <el-row class="order__section right">
          <el-col :span="24">
            <span
              class="order__status"
              :class="status[order.status].styleClazz"
            >{{ status[order.status].display }}</span>
          </el-col>
        </el-row>
        <el-row class="order__section metadata">
          <el-col :span="12">{{ order.createdTimeDisplay }}</el-col>
          <el-col :span="12" class="right">{{ order.orderNumber }}</el-col>
        </el-row>
        <el-row class="order__section right">
          <el-col :span="24">
            {{ mode[order.mode] }} | {{ paymentMethod[order.paymentMethod] }} |
            <strong>${{ order.amountDisplay }}</strong>
          </el-col>
        </el-row>
        <el-row class="order__section">
          <el-col :span="12">{{ order.customer.phone }}</el-col>
          <el-col
            :span="12"
            class="right"
          >{{ order.customer.firstName }}, {{ order.customer.surname }}</el-col>
        </el-row>
        <el-row class="order__section" v-if="order.shippingAddress">
          <el-col
            :span="24"
          >{{ order.shippingAddress.streetAddress }}, {{ order.shippingAddress.city }}, {{ order.shippingAddress.state }}, {{ order.shippingAddress.zipCode }}</el-col>
        </el-row>
      </div>
    </div>
    <div v-if="orders.length <= 0">
      <h3 class="centre">No order.</h3>
    </div>
    <div v-if="!display.isSignedIn">
      <p
        class="note"
      >Only the order placed recently will be shown here. All of your orders will be available once you sign in.</p>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import moment from "moment";
import { getById, getMine, own } from "@/api/order";
import logger from "@/utils/logger";
import { featureToggle } from "@/utils";
import { ORDER_STATUS, ORDER_MODE, ORDER_PAYMENT_METHOD } from "@/utils/order";

const name = "Orders";
const log = logger(name);

export default {
  name,
  data() {
    return {
      featureToggle,
      orders: [],
      unlinkedOrder: null,

      pagination: {
        page: 0,
        size: 10
      },

      display: {
        isLoading: true,
        isSignedIn: false,
        hasNextPage: true,
        dialog: {
          unlinkedOrder: {
            isVisible: false
          }
        }
      },

      status: ORDER_STATUS.reduce(
        (current, incoming) =>
          Object.assign(current, { [incoming.value]: incoming }),
        {}
      ),
      mode: ORDER_MODE.reduce(
        (current, incoming) =>
          Object.assign(current, { [incoming.value]: incoming.display }),
        {}
      ),
      paymentMethod: ORDER_PAYMENT_METHOD.reduce(
        (current, incoming) =>
          Object.assign(current, { [incoming.value]: incoming.display }),
        {}
      )
    };
  },
  computed: {
    ...mapGetters(["latestOrderId", "token", "customerId"])
  },
  async created() {
    if (!this.token) {
      log.info("Not signed in, load latest order");
      if (this.latestOrderId) {
        try {
          const order = await getById(this.latestOrderId);
          this._decorateOrder([order]);
          return (this.orders = [order]);
        } catch (err) {
          log.warn("Latest order load failed", err);
          return;
        } finally {
          this.display.isLoading = false;
        }
      }
      log.info("No latest order ID");
      this.display.isLoading = false;
      return;
    }
    log.info("Signed in, load my orders");
    this.display.isSignedIn = true;
    // await this.fetchData();
  },
  methods: {
    async fetchData() {
      if (!this.token) {
        log.info("Not signed in, not to load other orders");
        this.display.isLoading = false;
        this.orders = [];
        return;
      }
      log.info(
        `Loading orders, page=${this.pagination.page}, size=${this.pagination.size}`
      );
      this.display.isLoading = true;
      const orders = await getMine(this.pagination);
      this._decorateOrder(orders);
      this.orders = this.orders.concat(orders);
      if (orders.length < this.pagination.size) {
        this.display.hasNextPage = false;
      }
      this.display.isLoading = false;
    },

    onClickOrder(order) {
      log.info("Click order", order._id);
      this.$router.push(`/order/${order._id}`);
    },
    onClickBack() {
      this.$router.go(-1);
    },
    async onClickUnlinkedOrder() {
      this.display.isLoading = true;
      log.info("Click unlinked order, latest order id", this.latestOrderId);
      this.display.dialog.unlinkedOrder.isVisible = true;
      if (!this.latestOrderId) {
        log.info("No latest order ID");
        this.display.isLoading = false;
        return;
      }
      const order = await getById(this.latestOrderId);
      if (order.customerId === this.customerId) {
        this.display.isLoading = false;
        return;
      }
      this._decorateOrder([order]);
      this.unlinkedOrder = order;
      this.display.isLoading = false;
    },
    async onClickOwnOrder() {
      this.display.isLoading = true;
      await own(this.latestOrderId);
      await this.$store.dispatch("order/refreshLatestOrderId", "");
      this.display.dialog.unlinkedOrder.isVisible = false;
      this.display.isLoading = false;
      this.$message({
        message: "Order claimed successfully.",
        type: "success"
      });
      this.unlinkedOrder = null;
    },

    onClickMyReservations() {
      this.$router.push("/reservations");
    },

    async loadNextPage() {
      if (!this.display.hasNextPage) {
        log.info("No further results");
        return;
      }
      ++this.pagination.page;
      await this.fetchData();
    },

    _decorateOrder(orders) {
      orders.forEach(order => {
        order.amountDisplay = order.amount.toFixed(2);
        order.createdTimeDisplay = moment(order.createdTime).format(
          "HH:mm, D MMM YYYY"
        );
        order.mode = order.attributes.find(attr => attr.key === "mode").value;
        order.paymentMethod = order.attributes.find(
          attr => attr.key === "paymentMethod"
        ).value;
      });
    }
  }
};
</script>
<style scoped>
.order {
  margin: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 4px;
}

.order-list {
  margin: 1rem 0.5rem 0 0.5rem;
  height: 79vh;
  overflow: auto;
}

.order__section {
  padding-top: 0.2rem;
  padding-bottom: 0.2rem;
}

.order__section.metadata {
  font-size: 0.9rem;
}

.order__status {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

.note {
  margin: 0.5rem;
}
</style>